import {
	Conference,
	ConferenceManager,
	ManagedStream,
	Session,
} from '@quobis/sippojs';
import {BehaviorSubject, Observable, distinctUntilChanged, filter, firstValueFrom, map, switchMap, tap} from 'rxjs';

import {AuthService} from './AuthService';
import envService from './EnvService';

/*
in all services we create observables subscribed in components
 */

export class CallService {
	currentConference: Conference | undefined;

	/*
  localStream$, audioStream$ and remoteStreams$ observables used to render the conference streams
   */
	public localStream$: Observable<ManagedStream | undefined>;
	public audioStream$: Observable<ManagedStream | undefined>;
	public remoteStreams$: Observable<ManagedStream[]>;

	private conferenceStatusSubject = new BehaviorSubject<number | undefined>(undefined);
	public conferenceStatus$ = this.conferenceStatusSubject.asObservable();
	private conferenceManager$: Observable<ConferenceManager>;

	private mediaTypes = {
		audio: true,
		video: envService.HAS_VIDEO,
		screen: false,
	};

	constructor(
		private authService: AuthService,
	) {
		this.conferenceManager$ = authService.session$.pipe(
			filter((session): session is Session => session !== undefined),
			map(session => session.getConferenceManager()),
		);
		const conference$ = this.conferenceManager$.pipe(
			tap(conferenceManager =>
				conferenceManager.setDefaultMediaConstraints(this.mediaTypes)),
			switchMap(conferenceManager => conferenceManager.conferences$),
			map(conferences => conferences.at(-1)),
			filter((x): x is Conference => x !== undefined),
			tap(conference =>
				conference.localMediaHandler.setMediaTypes(this.mediaTypes)),
		);
		this.localStream$ = conference$.pipe(
			switchMap(conference => conference.localParticipant$),
			map(participant => participant?.streams.filter(stream => !stream.mediaTypes.audio).at(0)), // returns only not audio streams
		);
		this.conferenceStatus$ = conference$.pipe(
			switchMap(conference => conference.status$),
		);
		this.remoteStreams$ = conference$.pipe(
			switchMap(conference => conference.remoteParticipants$),
			switchMap(participants => participants?.map(
				participant => participant.streams.filter(
					(stream: ManagedStream) => !stream.mediaTypes.audio, // returns only not audio streams
				),
			)),
			distinctUntilChanged((previous, current) => {
				if (previous.length !== current.length) {
					return false;
				}
				const equals = previous.filter(oldManagedStream =>
					current.some(newManagedStream => oldManagedStream.mediaStream.id === newManagedStream.mediaStream.id));
				return equals.length === previous.length;
			}),
		);
		this.audioStream$ = conference$.pipe(
			switchMap(conference => conference.audioStream$),
		);
	}

	/**
   * Create a conference and join to the conference.
   * This new conference is added to the conferenceManager.conferences$
   * and automatically transfers streams to subscriptions
   * @return {!Promise<Conference>} Promise with the conference recently
   *     created.
   */
	async createAndJoin(callee: string): Promise<boolean | null> {
		const conferenceManager = await firstValueFrom(this.conferenceManager$);
		this.currentConference = await conferenceManager.createConference();
		if (!this.currentConference) {
			return Promise.reject();
		}
		await this.currentConference.join();
		const invite = await this.currentConference.inviteParticipants([callee]);
		const callResult = invite.filter(inviteResult => inviteResult.code === 200);
		return callResult.length > 0;
	}

	/**
   * Leave the conference and expel the rest of the users if we are the owner.
   * @return {!Promise<void>} Indicates if the process was completed.
   */
	async leave(): Promise<void> {
		if (!this.currentConference) {
			return Promise.reject();
		}
		return this.currentConference.leave(200).then(() => {
			this.currentConference = undefined;
		});
	}

	async toggleVideo(): Promise<boolean> {
		if (!this.currentConference) {
			return true;
		}
		const localMediaHandler = this.currentConference.localMediaHandler;
		const isMuted = localMediaHandler.isVideoMuted;
		await localMediaHandler.toggleVideo();
		return !isMuted;
	}

	toggleAudio(): boolean {
		if (!this.currentConference) {
			return true;
		}
		const localMediaHandler = this.currentConference.localMediaHandler;
		const isMuted = localMediaHandler.isAudioMuted;
		localMediaHandler.muteAudio(!isMuted);
		return !isMuted;
	}

}
